import type { ErrorInfo } from "react";
import {
  captureException,
  captureReactException,
  init,
  type Breadcrumb,
  type ErrorEvent,
} from "@sentry/react";

const QUERY_STRING_SEPARATOR = "?";

/**
 * 이 모듈이 빌드 환경에서 실제로 읽는 값만 담는다. 전역 `ImportMetaEnv` 는
 * `vite/client` 가 이미 선언하므로 건드리지 않는다.
 */
export interface ErrorReportingEnvironment {
  readonly MODE: string;
  readonly VITE_SENTRY_DSN?: string;
  readonly VITE_ENVIRONMENT?: string;
  readonly VITE_RELEASE?: string;
}

function removeQueryString(url: string): string {
  const separatorIndex = url.indexOf(QUERY_STRING_SEPARATOR);
  return separatorIndex === -1 ? url : url.slice(0, separatorIndex);
}

/**
 * OAuth 복귀(`?auth=oauth`)와 이메일 인증 링크가 query string 으로 자격 값을 실어
 * 나르므로, dataCollection 으로 수집을 끈 뒤에도 URL 문자열 자체에 남은 query 를 지운다.
 */
export function removeSensitiveRequestData(event: ErrorEvent): ErrorEvent {
  const request = event.request;
  if (!request) return event;

  return {
    ...event,
    request: {
      ...request,
      url: request.url ? removeQueryString(request.url) : request.url,
      query_string: undefined,
      cookies: undefined,
      headers: undefined,
      data: undefined,
    },
  };
}

export function removeSensitiveBreadcrumbData(
  breadcrumb: Breadcrumb
): Breadcrumb {
  const url = breadcrumb.data?.url;
  if (typeof url !== "string") return breadcrumb;

  return {
    ...breadcrumb,
    data: { ...breadcrumb.data, url: removeQueryString(url) },
  };
}

export function resolveRelease(
  environmentValues: ErrorReportingEnvironment
): string {
  const configuredRelease = environmentValues.VITE_RELEASE?.trim();
  if (configuredRelease) return configuredRelease;

  return `${__APP_VERSION__}+${__GIT_COMMIT_HASH__}`;
}

/**
 * DSN 이 설정된 경우에만 Sentry 를 초기화하고, 초기화 여부를 반환한다.
 * DSN 이 없으면 SDK 를 건드리지 않으므로 네트워크 요청이 한 건도 발생하지 않는다.
 */
export function initializeErrorReportingWhenConfigured(
  environmentValues: ErrorReportingEnvironment = import.meta.env
): boolean {
  const dataSourceName = environmentValues.VITE_SENTRY_DSN?.trim();
  if (!dataSourceName) return false;

  init({
    dsn: dataSourceName,
    environment:
      environmentValues.VITE_ENVIRONMENT?.trim() || environmentValues.MODE,
    release: resolveRelease(environmentValues),
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: { request: false, response: false },
      httpBodies: [],
      urlQueryParams: false,
    },
    // 기본값 'always' 는 원본 error.message 를 변형하므로, 메시지 문자열로 분기하는
    // 코드(ErrorBoundary 의 classifyError)가 깨질 수 있다. 보고 시에만 덧붙인다.
    enhanceFetchErrorMessages: "report-only",
    beforeSend: removeSensitiveRequestData,
    beforeBreadcrumb: removeSensitiveBreadcrumbData,
  });

  return true;
}

export function reportReactRenderError(
  error: unknown,
  errorInfo: ErrorInfo
): void {
  captureReactException(error, errorInfo);
}

export function reportVueComponentError(
  error: unknown,
  vueErrorInfo: string
): void {
  captureException(error, { extra: { vueErrorInfo } });
}
